import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Package, Plus, Search, ShieldCheck, Wrench, ExternalLink, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const fmtMoney = (n, ccy = "INR") =>
    n == null ? "—" : `${ccy === "INR" ? "₹" : ccy + " "}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const OWNER_OPTS = ["Self", "Spouse", "Child", "Parent", "Business", "Other"];
const STATUS_OPTS = ["Owned", "Sold", "Lost", "Damaged", "Returned"];
const CONDITION_OPTS = ["New", "Good", "Fair", "Poor"];

const EMPTY = {
    name: "", category: "Electronics", brand: "", model: "", serial_number: "",
    purchase_date: "", purchase_price: "", current_value: "", vendor: "",
    warranty_end_date: "", applecare_end_date: "",
    insurance_provider: "", insurance_policy_no: "", insurance_end_date: "",
    card_id: "", owner: "Self", location: "", condition: "Good", status: "Owned",
    notes: "",
};

export default function Assets() {
    const { hasRole } = useAuth();
    const [rows, setRows] = useState([]);
    const [cards, setCards] = useState([]);
    const [categories, setCategories] = useState([]);
    const [summary, setSummary] = useState({ total_count: 0, total_value: 0, by_category: [] });
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("");
    const [owner, setOwner] = useState("");
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);

    const load = useCallback(async () => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (category) params.set("category", category);
        if (owner) params.set("owner", owner);
        try {
            const [r, s] = await Promise.all([
                api.get(`/assets?${params.toString()}`),
                api.get("/assets/summary"),
            ]);
            setRows(r.data || []);
            setSummary(s.data || { total_count: 0, total_value: 0, by_category: [] });
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, [q, category, owner]);

    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data || []));
        api.get("/assets/meta/categories").then((r) => setCategories(r.data?.categories || []));
    }, []);
    useEffect(() => { load(); }, [load]);

    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { ...f };
            ["purchase_price", "current_value"].forEach((k) => {
                if (body[k] === "" || body[k] == null) delete body[k];
                else body[k] = Number(body[k]);
            });
            ["purchase_date", "warranty_end_date", "applecare_end_date", "insurance_end_date"].forEach((k) => {
                if (!body[k]) delete body[k];
            });
            if (!body.card_id) delete body.card_id;
            if (editingId) {
                await api.patch(`/assets/${editingId}`, body);
                toast.success("Asset updated");
            } else {
                await api.post("/assets", body);
                toast.success("Asset added");
            }
            setOpen(false);
            setEditingId(null);
            setF(EMPTY);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const openAdd = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
    const openEdit = (row) => {
        setEditingId(row.id);
        setF({
            name: row.name || "",
            category: row.category || "Electronics",
            brand: row.brand || "",
            model: row.model || "",
            serial_number: row.serial_number || "",
            purchase_date: (row.purchase_date || "").slice(0, 10),
            purchase_price: row.purchase_price ?? "",
            current_value: row.current_value ?? "",
            vendor: row.vendor || "",
            warranty_end_date: (row.warranty_end_date || "").slice(0, 10),
            applecare_end_date: (row.applecare_end_date || "").slice(0, 10),
            insurance_provider: row.insurance_provider || "",
            insurance_policy_no: row.insurance_policy_no || "",
            insurance_end_date: (row.insurance_end_date || "").slice(0, 10),
            card_id: row.card_id || "",
            owner: row.owner || "Self",
            location: row.location || "",
            condition: row.condition || "Good",
            status: row.status || "Owned",
            notes: row.notes || "",
        });
        setOpen(true);
    };

    const del = async (id, name) => {
        if (!window.confirm(`Delete asset "${name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/assets/${id}`);
            toast.success("Asset deleted");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const cardMap = useMemo(() => Object.fromEntries(cards.map((c) => [c.id, c])), [cards]);

    return (
        <div data-testid="assets-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Package size={12} /> Personal Inventory</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Every valuable thing you own.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Track invoices, serial numbers, warranties, AppleCare, insurance and service history for
                        every asset in one place. Warranty &amp; insurance expiries roll up automatically into the
                        Renewals tracker.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setF(EMPTY); } }}>
                    <DialogTrigger asChild>
                        <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="asset-add" onClick={openAdd}>
                            <Plus size={14} className="mr-1.5" /> Add asset
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="asset-add-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-display text-2xl">{editingId ? "Edit asset" : "New asset"}</DialogTitle>
                            <DialogDescription className="text-[13px] text-stone-500">
                                Add anything with real value &mdash; a MacBook, an air conditioner, jewellery, a camera.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <Label className="eyebrow">Name *</Label>
                                    <Input required value={f.name} onChange={setInput("name")} className="mt-1.5" placeholder="e.g. MacBook Pro 16&quot; M3 Max" data-testid="asset-name" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Category</Label>
                                    <Select value={f.category} onValueChange={set("category")}>
                                        <SelectTrigger className="mt-1.5" data-testid="asset-category"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="eyebrow">Brand</Label>
                                    <Input value={f.brand} onChange={setInput("brand")} className="mt-1.5" placeholder="Apple" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Model</Label>
                                    <Input value={f.model} onChange={setInput("model")} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Serial / IMEI</Label>
                                    <Input value={f.serial_number} onChange={setInput("serial_number")} className="mt-1.5 font-mono-figure" data-testid="asset-serial" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Vendor</Label>
                                    <Input value={f.vendor} onChange={setInput("vendor")} className="mt-1.5" placeholder="Apple India / Amazon / Croma" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Purchase date</Label>
                                    <Input type="date" value={f.purchase_date} onChange={setInput("purchase_date")} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Purchase price</Label>
                                    <Input type="number" step="0.01" value={f.purchase_price} onChange={setInput("purchase_price")} className="mt-1.5 font-mono-figure" placeholder="0" data-testid="asset-price" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Current value (optional)</Label>
                                    <Input type="number" step="0.01" value={f.current_value} onChange={setInput("current_value")} className="mt-1.5 font-mono-figure" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Bought on card</Label>
                                    <Select value={f.card_id} onValueChange={set("card_id")}>
                                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select card" /></SelectTrigger>
                                        <SelectContent>
                                            {cards.map((c) => (
                                                <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="eyebrow">Warranty ends</Label>
                                    <Input type="date" value={f.warranty_end_date} onChange={setInput("warranty_end_date")} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label className="eyebrow">AppleCare / extended ends</Label>
                                    <Input type="date" value={f.applecare_end_date} onChange={setInput("applecare_end_date")} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Insurance provider</Label>
                                    <Input value={f.insurance_provider} onChange={setInput("insurance_provider")} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Insurance ends</Label>
                                    <Input type="date" value={f.insurance_end_date} onChange={setInput("insurance_end_date")} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Owner</Label>
                                    <Select value={f.owner} onValueChange={set("owner")}>
                                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {OWNER_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="eyebrow">Location</Label>
                                    <Input value={f.location} onChange={setInput("location")} className="mt-1.5" placeholder="Bengaluru home / Office" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Condition</Label>
                                    <Select value={f.condition} onValueChange={set("condition")}>
                                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {CONDITION_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="eyebrow">Status</Label>
                                    <Select value={f.status} onValueChange={set("status")}>
                                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label className="eyebrow">Notes</Label>
                                <Textarea rows={2} value={f.notes} onChange={setInput("notes")} className="mt-1.5" placeholder="Space-grey · Sequoia · came with the Studio Display" />
                            </div>
                            <Button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="asset-submit">
                                {busy ? "Saving…" : editingId ? "Save changes" : "Save asset"}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Summary tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="rounded-lg border border-stone-200 bg-white p-4">
                    <div className="eyebrow">Assets tracked</div>
                    <div className="font-mono-figure text-3xl mt-1 text-stone-900" data-testid="assets-count">{summary.total_count}</div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-4">
                    <div className="eyebrow">Total value</div>
                    <div className="font-mono-figure text-3xl mt-1 text-stone-900">{fmtMoney(summary.total_value)}</div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-4 col-span-2">
                    <div className="eyebrow mb-2">By category</div>
                    <div className="flex flex-wrap gap-1.5">
                        {summary.by_category.slice(0, 8).map((c) => (
                            <span key={c.category} className="text-[11px] px-2 py-0.5 rounded-full border border-stone-200 bg-stone-50 text-stone-700">
                                {c.category} · <span className="font-mono-figure">{c.count}</span>
                            </span>
                        ))}
                        {summary.by_category.length === 0 && <span className="text-stone-400 text-xs">No assets yet.</span>}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[240px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / brand / serial…" className="pl-9" data-testid="asset-search" />
                </div>
                <Select value={category || "__all"} onValueChange={(v) => setCategory(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-44"><SelectValue placeholder="All categories" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">All categories</SelectItem>
                        {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={owner || "__all"} onValueChange={(v) => setOwner(v === "__all" ? "" : v)}>
                    <SelectTrigger className="w-36"><SelectValue placeholder="All owners" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all">All owners</SelectItem>
                        {OWNER_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {/* Rows */}
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Asset</th>
                                <th>Category</th>
                                <th>Owner</th>
                                <th className="text-right">Purchase</th>
                                <th className="text-right">Value</th>
                                <th>Warranty</th>
                                <th>Card</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const card = r.card_id ? cardMap[r.card_id] : null;
                                const anyExpiry = r.warranty_end_date || r.applecare_end_date;
                                return (
                                    <tr key={r.id} data-testid={`asset-row-${r.id}`}>
                                        <td>
                                            <div className="font-medium text-stone-900 flex items-center gap-2">
                                                <Link to={`/assets/${r.id}`} className="hover:underline decoration-stone-400 underline-offset-2">
                                                    {r.name}
                                                </Link>
                                                {r.status !== "Owned" && (
                                                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-stone-200 bg-stone-50 text-stone-500">{r.status}</span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-stone-500">
                                                {[r.brand, r.model].filter(Boolean).join(" · ") || "—"}
                                                {r.serial_number && <> · SN {r.serial_number}</>}
                                            </div>
                                        </td>
                                        <td className="text-stone-600 text-[12px]">{r.category}</td>
                                        <td className="text-stone-600 text-[12px]">{r.owner || "—"}</td>
                                        <td className="text-right text-stone-600 text-[12px]">{fmtDate(r.purchase_date) || "—"}</td>
                                        <td className="text-right font-mono-figure">{fmtMoney(r.current_value ?? r.purchase_price, r.currency)}</td>
                                        <td className="text-[12px]">
                                            {anyExpiry ? (
                                                <span className="inline-flex items-center gap-1 text-stone-600">
                                                    <ShieldCheck size={11} /> {fmtDate(anyExpiry)}
                                                </span>
                                            ) : <span className="text-stone-400">—</span>}
                                        </td>
                                        <td className="text-[12px] text-stone-600">
                                            {card ? `${card.bank_name} · ••${card.last_4}` : "—"}
                                        </td>
                                        <td>
                                            <div className="flex items-center justify-end gap-1">
                                                {hasRole("admin", "finance") && (
                                                    <>
                                                        <button onClick={() => openEdit(r)} title="Edit asset"
                                                                className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                                                                data-testid={`asset-edit-${r.id}`}>
                                                            <Pencil size={12} />
                                                        </button>
                                                        <button onClick={() => del(r.id, r.name)} title="Delete asset"
                                                                className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600"
                                                                data-testid={`asset-delete-${r.id}`}>
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </>
                                                )}
                                                <Link to={`/assets/${r.id}`} className="text-stone-500 hover:text-stone-900 text-xs inline-flex items-center gap-1">
                                                    Open <ExternalLink size={11} />
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr><td colSpan={8} className="text-center text-stone-500 py-14">
                                    <Package size={20} className="mx-auto mb-2 text-stone-400" />
                                    No assets yet &mdash; add your first one to start building your inventory.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// Small inline detail page — kept in same file to reduce nav sprawl.
export function AssetDetail() {
    // Minimal detail view lives at /assets/:id — see the routed component below.
    return null;
}

export { Wrench };
